import { gql, TypedDocumentNode } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import { ConfirmDialog } from '@repo/shared-client/components/blocks'
import { FormProvider, useForm } from '@repo/shared-client/form'
import { useTranslation } from '@web/src/lib/intl/react'
import { ArrowLeftRightIcon } from 'lucide-react'
import { useState } from 'react'
import toast from 'react-hot-toast'
import SFProfileSearchField from 'src/components/primatives/sf-profile-search-field/sf-profile-search-field'
import { EventInfo } from 'src/lib/fragments'
import type { TransferEventMutation, TransferEventMutationVariables } from 'types/graphql'

const TRANSFER_EVENT_MUTATION: TypedDocumentNode<TransferEventMutation, TransferEventMutationVariables> = gql`
  mutation TransferEventMutation($eventIdOrSlug: String!, $newOwnerId: String!) {
    transferEventOwnership(eventIdOrSlug: $eventIdOrSlug, newOwnerId: $newOwnerId) {
      ...EventInfo
    }
  }
  ${EventInfo}
`

type CustomModalProps = {
  open: boolean
  onOpenChange?: (details: { open: boolean }) => void
}

const EventTransferModal = ({
  eventIdOrSlug,
  onComplete,
  ...props
}: CustomModalProps & {
  eventIdOrSlug?: string
  onComplete?: () => void
}) => {
  const { t } = useTranslation()
  const formMethods = useForm()
  const [showSpinner, setShowSpinnerValue] = useState(false)
  const [profile, setProfile] = useState(null)

  const [transferEvent] = useMutation(TRANSFER_EVENT_MUTATION, {
    onCompleted: () => {
      closeDialog()
      toast.success(t('Event ownership transferred successfully'))
      onComplete?.()
    },
    onError: (error) => {
      toast.error(error.message)
      closeDialog()
    },
  })

  const onSubmit = () => {
    setShowSpinnerValue(true)
    transferEvent({
      variables: { eventIdOrSlug: eventIdOrSlug, newOwnerId: profile?.id },
    })
  }

  const closeDialog = () => {
    props?.onOpenChange?.({ open: false })
  }

  return (
    <ConfirmDialog
      beforeControls={
        <FormProvider {...formMethods}>
          <SFProfileSearchField
            excludeSelf={false}
            inputProps={{ placeholder: t('Search profiles here...') }}
            label={t('Select New Owner')}
            name="profile"
            onProfileSelected={(profileValue) => {
              setProfile(profileValue ?? null)
              return false
            }}
          />
        </FormProvider>
      }
      confirmationPlaceholder={t('Type confirm')}
      confirmationPrompt={t("Enter 'confirm' in the box below")}
      confirmationText="confirm"
      confirmDisabled={!profile}
      confirmText={t('Transfer')}
      icon={<ArrowLeftRightIcon />}
      loading={showSpinner}
      onConfirm={onSubmit}
      onOpenChange={(details) => {
        props?.onOpenChange?.(details)

        if (!details.open) {
          formMethods.reset()
          setShowSpinnerValue(false)
          setProfile(null)
        }
      }}
      open={props.open}
      text={t('You will no longer be able to edit this page if you transfer ownership.')}
      title={t('Transfer Event Ownership')}
      variant="delete"
    />
  )
}

export default EventTransferModal
